/**
 * RoundEndPanels — role-aware inline cards rendered inside each OLG panel
 * the moment the round completes. The design doc §8 is explicit: each person
 * answers on their own phone, privately and before the final score appears.
 *
 * - ConflictCheckInCard: shown in the Conflict panel. Three one-tap questions
 *   that feed Guidance / Control / Landing.
 * - ConflictCoachCheckInCard: shown in the CC panel. Three questions that
 *   feed Control / Landing.
 * - WaitingForHumansCard: shown in the Guide and Coach panels so they know
 *   the score is pending Conflict + CC input. Status chips show who's done.
 *
 * All three cards share one polling source: the `latestDebriefId` on the
 * room doc. Once the debrief exists they can read / write `humanAnswers`
 * directly, and when both sides have submitted the score blends.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Clock, FileText } from 'lucide-react';
import { OL } from './OperationListenParts';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('gntl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Pill = ({ v, active, onTap, testId }) => (
  <button
    onClick={onTap}
    data-testid={testId}
    data-active={active ? 'true' : 'false'}
    style={{
      padding: '7px 14px', borderRadius: 999,
      background: active ? OL.clay : 'transparent',
      border: `1px solid ${active ? OL.clay : OL.border2}`,
      color: active ? '#fff' : OL.text,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', cursor: 'pointer', touchAction: 'manipulation',
    }}
  >{v}</button>
);

const QuestionRow = ({ q, value, onChange, testIdBase }) => (
  <div style={{ padding: '10px 0', borderBottom: `1px solid ${OL.border}` }}>
    <div style={{ fontSize: 12, color: OL.text, marginBottom: 6, lineHeight: 1.45 }}>{q}</div>
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {['yes', 'partly', 'no'].map((v) => (
        <Pill key={v} v={v}
              active={value === v}
              onTap={() => onChange(v)}
              testId={`${testIdBase}-${v}`} />
      ))}
    </div>
  </div>
);

/**
 * Shared hook: polls the room until a debrief exists for the current round,
 * then exposes {debrief, submit}. Used by all three cards below.
 */
export function useRoundDebrief({ roomCode, isDemoRoom = false, pollMs = 1500 }) {
  const [debrief, setDebrief] = useState(null);
  const [debriefId, setDebriefId] = useState(null);
  const roomPath = isDemoRoom
    ? `/api/olg/admin/demo-room/${roomCode}`
    : `/api/olg/rooms/${roomCode}`;

  const refresh = useCallback(async () => {
    if (!roomCode) return;
    try {
      // Both regular + demo rooms live in olg_rooms, so GET /rooms/{code}
      // returns the public payload (with latestDebriefId) for either.
      const r = await fetch(`${API}/api/olg/rooms/${roomCode}`,
                             { headers: authHeader() }).then((x) => x.json());
      const nextId = r?.latestDebriefId;
      if (!nextId) return;
      try {
        const d = await fetch(`${API}/api/olg/debriefs/${nextId}`,
                               { headers: authHeader() }).then((x) => x.json());
        if (d && d.id) {
          setDebriefId(d.id);
          setDebrief(d);
        }
      } catch (_) { /* ignore */ }
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode]);

  useEffect(() => {
    if (!roomCode) return undefined;
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [roomCode, refresh, pollMs]);

  const submit = useCallback(async (role, answers) => {
    if (!debriefId) return null;
    try {
      const d = await fetch(`${API}/api/olg/debriefs/${debriefId}/human-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ role, ...answers }),
      }).then((x) => x.json());
      setDebrief(d);
      return d;
    } catch (_) { return null; }
  }, [debriefId]);

  return { debrief, debriefId, submit, refresh };
}

/** 3-question inline card for the Conflict role. */
export const ConflictCheckInCard = ({ roomCode, isDemoRoom = false, onOpenDebrief }) => {
  const { debrief, submit } = useRoundDebrief({ roomCode, isDemoRoom });
  const ha = debrief?.humanAnswers?.conflict || {};
  const [local, setLocal] = useState({});
  useEffect(() => { setLocal(ha); /* eslint-disable-next-line */ }, [debrief?.id]);
  const bind = (field) => (v) => {
    const next = { ...local, [field]: v };
    setLocal(next);
    submit('conflict', { q1: next.q1, q2: next.q2, q3: next.q3 });
  };
  const done = !!(ha.q1 && ha.q2 && ha.q3);
  if (!debrief) {
    return <WaitingShell role="conflict" />;
  }
  return (
    <div data-testid="ol-conflict-checkin"
      style={{
        margin: '12px 14px 0', padding: 14, borderRadius: 12,
        background: OL.surface2, border: `1.5px solid ${done ? OL.clay : OL.amber}`,
      }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: OL.textDim,
                    letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        Weigh in — Conflict
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, marginTop: 2 }}>
        {done ? 'Thanks — your view is saved.' : 'Three quick taps on how it felt.'}
      </div>
      <QuestionRow q="1 · Did you feel heard?"
                   value={local.q1} onChange={bind('q1')}
                   testIdBase="ol-conflict-q1" />
      <QuestionRow q="2 · Did the Guide stay steady when it got heated?"
                   value={local.q2} onChange={bind('q2')}
                   testIdBase="ol-conflict-q2" />
      <QuestionRow q="3 · Would you follow through on where it ended?"
                   value={local.q3} onChange={bind('q3')}
                   testIdBase="ol-conflict-q3" />
      {done && onOpenDebrief && (
        <button
          onClick={() => onOpenDebrief(debrief)}
          data-testid="ol-conflict-open-debrief"
          style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 10,
            background: OL.clay, border: `1px solid ${OL.clay}`, color: '#fff',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        ><FileText size={13} /> View post-round analysis</button>
      )}
    </div>
  );
};

/** 3-question inline card for the Conflict Coach role. */
export const ConflictCoachCheckInCard = ({ roomCode, isDemoRoom = false, onOpenDebrief }) => {
  const { debrief, submit } = useRoundDebrief({ roomCode, isDemoRoom });
  const ha = debrief?.humanAnswers?.conflictCoach || {};
  const [local, setLocal] = useState({});
  useEffect(() => { setLocal(ha); /* eslint-disable-next-line */ }, [debrief?.id]);
  const bind = (field) => (v) => {
    const next = { ...local, [field]: v };
    setLocal(next);
    submit('conflictCoach', { q1: next.q1, q2: next.q2, q3: next.q3 });
  };
  const done = !!(ha.q1 && ha.q2 && ha.q3);
  if (!debrief) {
    return <WaitingShell role="conflictCoach" />;
  }
  return (
    <div data-testid="ol-cc-checkin"
      style={{
        margin: '12px 14px 0', padding: 14, borderRadius: 12,
        background: OL.surface2, border: `1.5px solid ${done ? OL.clay : OL.amber}`,
      }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: OL.textDim,
                    letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        Weigh in — Conflict Coach
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, marginTop: 2 }}>
        {done ? 'Thanks — your view is saved.' : 'Three quick taps on how the Guide held it.'}
      </div>
      <QuestionRow q="1 · Did the heat come down because of what the Guide did?"
                   value={local.q1} onChange={bind('q1')}
                   testIdBase="ol-cc-q1" />
      <QuestionRow q="2 · Did the Guide's voice stay calm and steady when you raised the threat?"
                   value={local.q2} onChange={bind('q2')}
                   testIdBase="ol-cc-q2" />
      <QuestionRow q="3 · Did it end with something real the Conflict could act on?"
                   value={local.q3} onChange={bind('q3')}
                   testIdBase="ol-cc-q3" />
      {done && onOpenDebrief && (
        <button
          onClick={() => onOpenDebrief(debrief)}
          data-testid="ol-cc-open-debrief"
          style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 10,
            background: OL.clay, border: `1px solid ${OL.clay}`, color: '#fff',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        ><FileText size={13} /> View post-round analysis</button>
      )}
    </div>
  );
};

const WaitingShell = ({ role, children }) => (
  <div data-testid={`ol-${role}-waiting-shell`}
    style={{
      margin: '12px 14px 0', padding: 14, borderRadius: 12,
      background: OL.surface2, border: `1.5px dashed ${OL.border2}`,
    }}>
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: OL.textDim, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
      <Clock size={12} /> Round ending…
    </div>
    {children}
  </div>
);

/** "Waiting for human weigh-in" card + end-of-round prompt for Guide and
 *  Coach panels. Rolls through three visual states:
 *   1. Archiving (brief) — "Scoring the round…"
 *   2. Waiting for Conflict / CC check-ins — status chips
 *   3. Scored — surface three choices: View analysis · Next round · End game
 */
export const WaitingForHumansCard = ({
  roomCode, isDemoRoom = false, roleMode = '4p',
  onOpenDebrief, onNextRound, onEndGame, canControl = true,
}) => {
  const { debrief } = useRoundDebrief({ roomCode, isDemoRoom });
  const cf = debrief?.humanAnswers?.conflict || {};
  const cc = debrief?.humanAnswers?.conflictCoach || {};
  const cfDone = !!(cf.q1 && cf.q2 && cf.q3);
  const ccDone = (roleMode === '2p' || roleMode === '3p') ? true : !!(cc.q1 && cc.q2 && cc.q3);
  const allDone = cfDone && ccDone;
  const scores = debrief?.scores;
  const summary = useMemo(() => {
    if (!scores) return null;
    return { rating: scores.roundRating, label: scores.label, tier: scores.tier };
  }, [scores]);

  // --- state 1: still archiving ---
  if (!debrief) {
    return (
      <WaitingShell role="guide-coach">
        <div style={{ fontSize: 13, color: OL.text, marginTop: 4 }}>
          Scoring the round…
        </div>
      </WaitingShell>
    );
  }

  const borderColor = allDone ? OL.clay : OL.amber;
  return (
    <div data-testid="ol-waiting-humans"
      style={{
        margin: '12px 14px 0', padding: 14, borderRadius: 12,
        background: OL.surface2, border: `1.5px solid ${borderColor}`,
      }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: OL.textDim,
                    letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        Round complete
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, marginTop: 2 }}>
        Scoring complete.
      </div>

      {/* Status chips — only shown while check-ins are still pending */}
      {!allDone && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: OL.textDim }}>Still waiting on:</span>
          {!cfDone && <StatusChip label="Conflict" done={false} />}
          {!ccDone && (roleMode !== '2p' && roleMode !== '3p') && (
            <StatusChip label="Conflict Coach" done={false} />
          )}
        </div>
      )}

      {/* Overall score strip */}
      {summary && (
        <div style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: OL.surface, border: `1px solid ${OL.border}`,
          fontSize: 12, color: OL.text,
        }}>
          <span style={{ color: OL.textDim }}>Rating:</span>{' '}
          <b>{summary.rating}</b> {summary.label} · tier <b>{summary.tier}</b>
          {!allDone && <span style={{ color: OL.textDim }}> · will refine as check-ins land</span>}
        </div>
      )}

      {/* 3-choice prompt — surface the moment scoring exists. */}
      <div
        data-testid="ol-roundend-prompt"
        style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}
      >
        {onOpenDebrief && (
          <button
            onClick={() => onOpenDebrief(debrief)}
            data-testid="ol-waiting-open-debrief"
            style={{
              flex: '1 1 160px', padding: '10px 12px', borderRadius: 10,
              background: OL.clay, border: `1px solid ${OL.clay}`, color: '#fff',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          ><FileText size={13} /> View analysis</button>
        )}
        {canControl && onNextRound && (
          <button
            onClick={onNextRound}
            data-testid="ol-waiting-next-round"
            style={{
              flex: '1 1 120px', padding: '10px 12px', borderRadius: 10,
              background: OL.surface, border: `1px solid ${OL.border2}`, color: OL.text,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >New round</button>
        )}
        {canControl && onEndGame && (
          <button
            onClick={onEndGame}
            data-testid="ol-waiting-end-game"
            style={{
              flex: '1 1 100px', padding: '10px 12px', borderRadius: 10,
              background: 'transparent', border: `1px solid ${OL.danger}66`,
              color: '#F87171', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >End game</button>
        )}
      </div>
    </div>
  );
};

const StatusChip = ({ label, done }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 999,
    background: done ? 'rgba(59,122,59,0.15)' : OL.surface,
    border: `1px solid ${done ? '#3B7A3B' : OL.border2}`,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: done ? '#3B7A3B' : OL.textDim,
  }}>
    {done ? <Check size={10} /> : <Clock size={10} />}{label}
  </span>
);
